import { Form, Formik } from "formik";
import { object, string } from "yup";
import { useState, type ReactNode } from "react";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import PasswordInput from "../UI/PasswordInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import useAccount from "../../hooks/use-account";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth.js";
// @ts-expect-error pure js
import { stringMax } from "../../models/utils";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import Button from "../UI/Button";
import ModalDialog from "../UI/ModalDialog";
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_REGEX,
} from "../../utils/constants/validation";
import styles from "./ChangeEmailForm.module.css";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";

interface ChangeEmailFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface RequestValues {
  new_mail: string;
  password: string;
}

interface VerifyValues {
  code: string;
}

const RESEND_COOLDOWN_MS = 30_000;

const REQUEST_SCHEMA = object({
  new_mail: string()
    .matches(EMAIL_REGEX, EMAIL_INVALID_MESSAGE)
    .required("L'adresse e-mail est obligatoire"),
  password: stringMax(250).required("Mot de passe requis"),
});

const VERIFY_SCHEMA = object({
  code: string()
    .required("Code requis")
    .matches(/^\d{6}$/, "Le code doit contenir 6 chiffres"),
});

function extractErrorMessage(err: unknown): ReactNode {
  const axiosError = err as { response?: { data?: { error?: string } } };
  return axiosError.response?.data?.error ?? handleAPIError(err);
}

export default function ChangeEmailForm({
  open,
  onOpenChange,
}: ChangeEmailFormProps) {
  const {
    account,
    requestEmailChange,
    verifyEmailChange,
    resendEmailChangeCode,
  } = useAccount();
  const { buildAndSetAuth } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [newMail, setNewMail] = useState("");
  const [error, setError] = useState<ReactNode>("");
  const [info, setInfo] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);

  const isGoogleAccount = account.provider === "google";

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setToken(null);
      setNewMail("");
      setError("");
      setInfo("");
    }
    onOpenChange(nextOpen);
  }

  async function handleRequest(values: RequestValues) {
    setError("");
    try {
      const data = await requestEmailChange(values);
      setToken(data.token);
      setNewMail(values.new_mail);
    } catch (err: unknown) {
      setError(extractErrorMessage(err));
    }
  }

  async function handleVerify(values: VerifyValues) {
    setError("");
    try {
      const data = await verifyEmailChange({ token, code: values.code });
      buildAndSetAuth(data);
      handleOpenChange(false);
    } catch (err: unknown) {
      setError(extractErrorMessage(err));
    }
  }

  const [isResending, handleResend] = useLoader(async function () {
    setError("");
    setInfo("");
    try {
      await resendEmailChangeCode(token);
      setInfo("Un nouvel email contenant votre code vient d'être envoyé.");
      setResendDisabled(true);
      setTimeout(() => setResendDisabled(false), RESEND_COOLDOWN_MS);
    } catch (err: unknown) {
      setError(extractErrorMessage(err));
    }
  });

  return (
    <ModalDialog
      size="sm"
      open={open}
      onOpenChange={handleOpenChange}
      header="Modifier l'adresse e-mail de connexion"
    >
      {isGoogleAccount ? (
        <Text>
          Votre connexion passe par Google : l'adresse e-mail ne peut pas être
          modifiée ici. Veuillez contactez le support.
        </Text>
      ) : token === null ? (
        <Formik<RequestValues>
          key="request"
          initialValues={{ new_mail: "", password: "" }}
          validationSchema={REQUEST_SCHEMA}
          onSubmit={handleRequest}
        >
          <Form className={styles.form}>
            <Text color="muted">
              Un code de confirmation à 6 chiffres sera envoyé à la nouvelle
              adresse.
            </Text>
            <TextInput
              type="email"
              name="new_mail"
              label="Nouvelle adresse e-mail"
              placeholder="contact@qalimo.fr"
              marginForm=""
            />
            <PasswordInput
              name="password"
              label="Mot de passe actuel"
              autoComplete="current-password"
              marginForm=""
            />
            <FormikSubmitButton>Recevoir le code</FormikSubmitButton>
            <Callout variant="danger">{error}</Callout>
          </Form>
        </Formik>
      ) : (
        <Formik<VerifyValues>
          key="verify"
          initialValues={{ code: "" }}
          validationSchema={VERIFY_SCHEMA}
          onSubmit={handleVerify}
        >
          <Form className={styles.form}>
            <Text>
              Un code à 6 chiffres vient d'être envoyé à {newMail}. Pensez à
              vérifier vos courriers indésirables.
            </Text>
            <TextInput
              type="text"
              name="code"
              label="Code de confirmation"
              placeholder="Entrez le code reçu par mail"
              autoComplete="one-time-code"
              inputMode="numeric"
              marginForm=""
            />
            <FormikSubmitButton>Valider</FormikSubmitButton>
            <div>
              <Text variant="deci" color="muted" as="span">
                Vous n'avez rien reçu ?
              </Text>{" "}
              <Button
                variant="tertiary"
                size="sm"
                disabled={isResending || resendDisabled}
                onClick={handleResend}
                isLoading={isResending}
              >
                Renvoyer le code
              </Button>
            </div>
            <Callout variant="success">{info}</Callout>
            <Callout variant="danger">{error}</Callout>
          </Form>
        </Formik>
      )}
    </ModalDialog>
  );
}
